/** Admin page (/admin/feedback): tester reports, newest first, with screenshots and snapshots. `?id=` opens one. */
import { useEffect, useState } from 'react';
import type { FeedbackDetail, FeedbackSummary } from '@kadice/shared';
import { api, feedbackImageUrl } from '../api';

const when = (iso: string) => new Date(iso).toLocaleString();

export function FeedbackAdmin() {
  const [list, setList] = useState<FeedbackSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(
    () => Number(new URLSearchParams(location.search).get('id')) || null,
  );

  useEffect(() => {
    api.listFeedback().then(setList, (err: unknown) => setError(err instanceof Error ? err.message : 'Failed.'));
  }, []);

  return (
    <div className="admin-feedback">
      <header className="row">
        <h2>Feedback reports</h2>
        <span className="spacer" />
        <a href="/admin/players">Players</a>
        <a href="/admin/games">Game logs</a>
        <a href="/">Back to game</a>
      </header>
      {error && <p className="error">{error}</p>}
      {list?.length === 0 && <p className="muted">No reports yet.</p>}
      <div className="admin-feedback-body">
        <ul className="admin-feedback-list">
          {list?.map((r) => (
            <li key={r.id}>
              <button className={openId === r.id ? 'open' : ''} onClick={() => setOpenId(r.id)}>
                <span className={`badge cat-${r.category}`}>{r.category}</span> <strong>#{r.id}</strong> {r.title}
                <span className="muted small">
                  {r.playerName} · {when(r.createdAt)}
                  {r.gameId !== null && ` · game ${r.gameId}`}
                  {r.imageCount > 0 && ` · ${r.imageCount} image${r.imageCount === 1 ? '' : 's'}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {openId !== null && <Report key={openId} id={openId} />}
      </div>
    </div>
  );
}

function Report({ id }: { id: number }) {
  const [report, setReport] = useState<FeedbackDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getFeedback(id).then(setReport, (err: unknown) => setError(err instanceof Error ? err.message : 'Failed.'));
  }, [id]);

  if (error) return <p className="error">{error}</p>;
  if (!report) return <p className="muted">Loading…</p>;
  return (
    <article className="admin-feedback-report">
      <h3>
        #{report.id} · {report.category} · {report.playerName} (player {report.playerId})
      </h3>
      <p className="muted small">
        {when(report.createdAt)}
        {report.gameId !== null && (
          <>
            {' · '}
            <a href={`/admin/games?id=${report.gameId}`}>game {report.gameId}</a>
          </>
        )}
        {report.userAgent && ` · ${report.userAgent}`}
      </p>
      <p className="admin-feedback-text">{report.body}</p>
      <div className="admin-feedback-images">
        {report.imageIds.map((imageId) => (
          <a key={imageId} href={feedbackImageUrl(report.id, imageId)} target="_blank" rel="noreferrer">
            <img src={feedbackImageUrl(report.id, imageId)} alt={`Attachment ${imageId}`} />
          </a>
        ))}
      </div>
      <details>
        <summary>Client snapshot (what the reporter's browser showed)</summary>
        <pre>{JSON.stringify(report.clientSnapshot, null, 2)}</pre>
      </details>
      <details>
        <summary>Server snapshot (full game state, hidden hands included)</summary>
        <pre>{report.serverSnapshot === null ? 'Not in a game.' : JSON.stringify(report.serverSnapshot, null, 2)}</pre>
      </details>
    </article>
  );
}
